import { Pokemon } from "prisma-layer";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokeNames } from "types/pokemon/poke-names";
import { purityToStatBonus, calculatePurity } from "./talent-utils";

type PokemonForStats = Pick<Pokemon, "base_pokemon_name" | "level" | "talents">;

// Must match backend constants from game/duel-engine/utils/stat-scaling.ts
const LEVEL_SCALE_FACTOR = 0.03; // 3% per level
const HP_MULTIPLIER = 2.75;

/**
 * Calculate the final stat value for a pokemon including level scaling and purity bonus
 */
 const calculatePokemonStat = (
  baseStat: number,
  level: number,
  statBonusPercentage: number,
): number => {
  // Level scaling: simple linear multiplier starting from level 1
  // Level 1 = 1.0x multiplier, each level adds 3%
  const levelMultiplier = 1.0 + (level - 1) * LEVEL_SCALE_FACTOR;

  // Apply level scaling to base stat
  let finalStat = baseStat * levelMultiplier;

  // Apply purity bonus percentage
  const purityMultiplier = 1.0 + statBonusPercentage / 100;
  finalStat *= purityMultiplier;


  return Math.round(finalStat);
};

/**
 * Get all calculated stats for a pokemon
 */
export const getPokemonStats = (pokemon: PokemonForStats, overrideLevel?: number) => {
  if (!pokemon) return null;

  const pokeName = pokemon.base_pokemon_name as PokeNames;
  const pokeBaseData = POKE_DATA[pokeName];

  if (!pokeBaseData) return null;

  const baseStats = pokeBaseData.stats;
  const pokemonTypes = pokeBaseData.types
    .filter((type) => type !== undefined)
    .map((type) => type.toString());
  const statBonusPercentage = purityToStatBonus(
    calculatePurity(pokemon.talents.map(String), pokemonTypes),
  );

  return {
    ability_power: calculatePokemonStat(
      baseStats.ability_power,
      overrideLevel ?? pokemon.level,
      statBonusPercentage,
    ),
    resistance: calculatePokemonStat(
      baseStats.resistance,
      overrideLevel ?? pokemon.level,
      statBonusPercentage,
    ),
    speed: calculatePokemonStat(
      baseStats.speed,
      overrideLevel ?? pokemon.level,
      statBonusPercentage,
    ),
 
  };
};
